import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Select,
  VStack,
  HStack,
  Text,
  Badge,
  useToast,
  Avatar,
} from '@chakra-ui/react';
import rolePermissionService from 'services/rolePermissionService';

export default function UserRoleModal({ isOpen, onClose, user, roles, onSuccess }) {
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (isOpen && user) {
      setSelectedRoleId(user.role?.id || '');
    }
  }, [isOpen, user]);

  const handleSubmit = async () => {
    if (!selectedRoleId) {
      toast({
        title: 'Error',
        description: 'Selecciona un rol',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      await rolePermissionService.assignRoleToUser(user.id, parseInt(selectedRoleId));
      toast({
        title: 'Éxito',
        description: 'Rol asignado correctamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onSuccess();
    } catch (error) {
      console.error('Error assigning role:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.message || error.message || 'Error al asignar el rol';
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedRoleId('');
    onClose();
  };

  const getRoleBadgeColor = (roleName) => {
    switch (roleName) {
      case 'Admin': return 'purple';
      case 'Mecanico': return 'blue';
      case 'Recepcionista': return 'green';
      default: return 'gray';
    }
  };

  const selectedRole = roles.find(role => role.id === parseInt(selectedRoleId));

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Asignar Rol a Usuario
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Información del usuario */}
            <HStack spacing={3} p={3} bg="gray.50" borderRadius="md">
              <Avatar size="sm" name={user?.name} />
              <VStack align="start" spacing={0}>
                <Text fontWeight="bold">{user?.name}</Text>
                <Text fontSize="sm" color="gray.600">{user?.email}</Text>
                <HStack>
                  <Text fontSize="xs" color="gray.500">Rol actual:</Text>
                  {user?.role ? (
                    <Badge size="sm" colorScheme={getRoleBadgeColor(user.role.name)}>
                      {user.role.name}
                    </Badge>
                  ) : (
                    <Badge size="sm" colorScheme="red">Sin Rol</Badge>
                  )}
                </HStack>
              </VStack>
            </HStack>

            {/* Selector de rol */}
            <FormControl isRequired>
              <FormLabel>Seleccionar Nuevo Rol</FormLabel>
              <Select
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                placeholder="Seleccionar rol..."
              >
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name} - {role.description}
                  </option>
                ))}
              </Select>
            </FormControl>

            {/* Vista previa del rol seleccionado */}
            {selectedRole && (
              <VStack align="start" spacing={2} p={3} bg="gray.50" borderRadius="md">
                <HStack>
                  <Text fontSize="sm" fontWeight="bold">Rol seleccionado:</Text>
                  <Badge colorScheme={getRoleBadgeColor(selectedRole.name)}>
                    {selectedRole.name}
                  </Badge>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  {selectedRole.description}
                </Text>
              </VStack>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="gray"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="Asignando..."
            isDisabled={!selectedRoleId}
          >
            Asignar Rol
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}